import api from "../../../../services/api";

export const MARKETPLACE_BASE_URL = "/api/v1/novatra/marketplace";
const unwrap = (response) => response.data?.data ?? response.data;
const get = (url, params) => api.get(url, { params }).then(unwrap);
const post = (url, body) => api.post(url, body).then(unwrap);
const put = (url, body) => api.put(url, body).then(unwrap);
const normalizePurchaseOrder = (po) => {
  const deal = po?.dealId && typeof po.dealId === "object" ? po.dealId : {};
  const listing = deal.listingId && typeof deal.listingId === "object" ? deal.listingId : {};
  const seller = po?.sellerId || deal.sellerId;
  return {
    ...po,
    listingNumber: listing.listingNumber,
    materialName: listing.materialName || deal.materialId?.name,
    sellerName: listing.sellerName || seller?.name,
    quantity: po.items?.[0]?.quantity ?? deal.agreedQuantity,
    uom: po.items?.[0]?.uom || deal.uom,
    sellerAskingRate: po.items?.[0]?.rate ?? deal.agreedRate,
    currency: po.currency || deal.currency,
    seller: seller ? { ...seller, name: seller.name } : undefined,
    listing: { ...listing, uom: po.items?.[0]?.uom || deal.uom },
  };
};
const normalizePurchaseOrders = (data) => ({
  ...data,
  items: (data?.items || []).map(normalizePurchaseOrder),
});
const normalizeGoodsReceipt = (receipt) => {
  const po = receipt?.purchaseOrderId || {};
  const deal = receipt?.dealId || po.dealId || {};
  const item = receipt?.items?.[0] || {};
  const material = item.materialId && typeof item.materialId === "object" ? item.materialId : {};
  const listing = { ...(deal.listingId || {}), materialName: material.name || deal.materialId?.name || deal.listingId?.materialName, quantity: item.receivedQuantity ?? item.orderedQuantity ?? deal.agreedQuantity, availableQuantity: item.acceptedQuantity, uom: item.uom || deal.uom, sellerAskingRate: deal.agreedRate, currency: deal.currency };
  return { ...receipt, receiptNumber: receipt.goodsReceiptNumber || receipt.receiptNumber, status: receipt.stockPosted ? "POSTED" : receipt.status || item.inspectionStatus, purchaseOrder: po, seller: receipt.sellerId || po.sellerId, listing, purchaseOrderQuantity: item.orderedQuantity ?? deal.agreedQuantity, previouslyReceived: item.previouslyReceivedQuantity ?? 0, uom: item.uom || deal.uom, materialName: listing.materialName, receivedQuantity: item.receivedQuantity, acceptedQuantity: item.acceptedQuantity, rejectedQuantity: item.rejectedQuantity, inspectionRequired: item.inspectionRequired, inspectionStatus: item.inspectionStatus, inspector: item.inspector, inspectionDate: item.inspectionDate, qualityRemarks: item.qualityRemarks, pendingQuantity: Math.max(0, Number(item.orderedQuantity ?? deal.agreedQuantity ?? 0) - Number(item.previouslyReceivedQuantity ?? 0) - Number(item.receivedQuantity ?? 0)), items: (receipt.items || []).map((row) => ({ ...row, materialName: row.materialId?.name || deal.materialId?.name, quantity: row.receivedQuantity ?? row.orderedQuantity, availableQuantity: row.acceptedQuantity, sellerAskingRate: deal.agreedRate, currency: deal.currency })) };
};
const normalizeGoodsReceipts = (data) => ({ ...data, items: (data?.items || []).map(normalizeGoodsReceipt) });
const upload = (file) => {
  const body = new FormData();
  body.append("file", file);
  return api
    .post("/api/v1/novatra/uploads", body, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then(unwrap);
};
const importListings = (file, { submit = false } = {}) => {
  const body = new FormData();
  body.append("file", file);
  body.append("submit", String(submit));
  return api
    .post(`${MARKETPLACE_BASE_URL}/material-listings/import`, body, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then(unwrap);
};

// All workflow transitions are delegated to the backend; this service contains no status logic.
export const materialMarketplaceApi = {
  getDashboard: () => get(`${MARKETPLACE_BASE_URL}/dashboard`),
  createListing: (payload) => post(`${MARKETPLACE_BASE_URL}/material-listings`, payload),
  /**
   * Create material listings in bulk from an Excel workbook (.xlsx/.xls) or CSV.
   * The API validates each row and returns created/failed row counts and errors.
   */
  importListings,
  updateListing: (id, payload) => put(`${MARKETPLACE_BASE_URL}/material-listings/${id}`, payload),
  uploadListingImage: upload,
  // The backend reads remarks from the request body, so always send an object
  // even when the seller has no remarks to add.
  submitListing: (id, payload = {}) => post(`${MARKETPLACE_BASE_URL}/material-listings/${id}/submit`, payload),
  getListings: (params) => get(`${MARKETPLACE_BASE_URL}/material-listings`, params),
  getListingById: (id) => get(`${MARKETPLACE_BASE_URL}/material-listings/${id}`),
  getWorkflows: () => get(`${MARKETPLACE_BASE_URL}/workflows`),
  createWorkflow: (payload) => post(`${MARKETPLACE_BASE_URL}/workflows`, payload),
  setDefaultWorkflow: (id) => post(`${MARKETPLACE_BASE_URL}/workflows/${id}/default`),
  getReviewWorklist: (params) => get(`${MARKETPLACE_BASE_URL}/business/material-reviews`, params),
  getReviewById: (id) => get(`${MARKETPLACE_BASE_URL}/business/material-reviews/${id}`),
  verifyListing: (id, payload) => post(`${MARKETPLACE_BASE_URL}/business/material-reviews/${id}/verify`, payload),
  requestClarification: (id, payload) => post(`${MARKETPLACE_BASE_URL}/business/material-reviews/${id}/request-clarification`, payload),
  approveForNegotiation: (id, payload) => post(`${MARKETPLACE_BASE_URL}/business/material-reviews/${id}/approve-for-negotiation`, payload),
  rejectListing: (id, payload) => post(`${MARKETPLACE_BASE_URL}/business/material-reviews/${id}/reject`, payload),
  createNegotiation: (payload) => post(`${MARKETPLACE_BASE_URL}/negotiations`, payload),
  getNegotiations: (params) => get(`${MARKETPLACE_BASE_URL}/negotiations`, params),
  getNegotiation: (id) => get(`${MARKETPLACE_BASE_URL}/negotiations/${id}`),
  createOffer: (id, payload) => post(`${MARKETPLACE_BASE_URL}/negotiations/${id}/offers`, payload),
  acceptOffer: (id, offerId, payload) => {
    if (typeof offerId === "object") {
      payload = offerId;
      offerId = payload.offerId || payload.id;
    }
    return post(`${MARKETPLACE_BASE_URL}/negotiations/${id}/offers/${offerId}/accept`, payload);
  },
  rejectOffer: (id, offerId, payload) => {
    if (typeof offerId === "object") {
      payload = offerId;
      offerId = payload.offerId || payload.id;
    }
    return post(`${MARKETPLACE_BASE_URL}/negotiations/${id}/offers/${offerId}/reject`, payload);
  },
  // The backend currently exposes procurement-deal detail/actions only; it
  // does not expose a marketplace approvals worklist endpoint.
  getApprovals: (params) => get(`${MARKETPLACE_BASE_URL}/procurement-deals`, params),
  getProcurementDeal: (id) => get(`${MARKETPLACE_BASE_URL}/procurement-deals/${id}`),
  submitDealForApproval: (id, payload) => post(`${MARKETPLACE_BASE_URL}/procurement-deals/${id}/submit-for-approval`, payload),
  approveDeal: (id, payload = {}) => post(`${MARKETPLACE_BASE_URL}/procurement-deals/${id}/approve`, { ...payload, comments: payload.comments ?? payload.remarks }),
  rejectDeal: (id, payload = {}) => post(`${MARKETPLACE_BASE_URL}/procurement-deals/${id}/reject`, { ...payload, comments: payload.comments ?? payload.remarks }),
  returnForNegotiation: (id, payload = {}) => post(`${MARKETPLACE_BASE_URL}/procurement-deals/${id}/return-for-negotiation`, { ...payload, comments: payload.comments ?? payload.remarks }),
  createPurchaseOrder: (id, payload) => post(`${MARKETPLACE_BASE_URL}/procurement-deals/${id}/create-purchase-order`, payload),
  // There is no purchase-order collection endpoint yet. Keep the method so
  // the page loader is callable and use the detail endpoint for linked POs.
  getPurchaseOrders: (params) => get(`${MARKETPLACE_BASE_URL}/purchase-orders`, params).then(normalizePurchaseOrders),
  getPurchaseOrder: (id) => get(`${MARKETPLACE_BASE_URL}/purchase-orders/${id}`).then(normalizePurchaseOrder),
  createGoodsReceipt: (id, payload) => post(`${MARKETPLACE_BASE_URL}/purchase-orders/${id}/goods-receipt`, payload),
  getGoodsReceipts: (params) => get(`${MARKETPLACE_BASE_URL}/goods-receipts`, params).then(normalizeGoodsReceipts),
  getGoodsReceipt: (id) => get(`${MARKETPLACE_BASE_URL}/goods-receipts/${id}`).then(normalizeGoodsReceipt),
  // Stock posting is currently available only as a receipt action.
  getStockWorklist: (params) => get(`${MARKETPLACE_BASE_URL}/goods-receipts`, params),
  postGoodsReceiptToStock: (id) => post(`${MARKETPLACE_BASE_URL}/goods-receipts/${id}/post-stock`),
};
